import { Pagination } from '@/shared/components/Pagination';
import {
  CAlert,
  CBadge,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react';
import React from 'react';
import type { BillingAttempt } from '../../types';
import { getBillingStatusBadgeColor, getPaymentStatusBadgeColor } from '../../utils/badgeColors';
import {
  formatDateTime,
  formatStatusDetails,
  getPaymentStatusDisplayValue,
  getShopifyOrderUrl,
  getSourceDisplayValue,
  getStatusDisplayValue,
} from '../../utils/formatting';

interface BillingAttemptsTableProps {
  billingAttempts: BillingAttempt[];
  loading: boolean;
  error: string | null;
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}

export const BillingAttemptsTable: React.FC<BillingAttemptsTableProps> = ({
  billingAttempts,
  loading,
  error,
  page,
  perPage,
  total,
  totalPages,
  onPageChange,
  onPerPageChange,
}) => {
  if (loading) {
    return <div className="text-center">Loading...</div>;
  }

  return (
    <>
      {error && <CAlert color="danger">{error}</CAlert>}
      {!error && (
        <>
          <CAlert color="warning">
            A successful billing attempt indicates that a subscription order was successfully{' '}
            <strong>created</strong> but not necessarily <strong>paid</strong> yet. Please always
            check the payment status too.
          </CAlert>
          {billingAttempts.length > 0 ? (
            <>
              <CTable hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell className="text-nowrap">Initiated At</CTableHeaderCell>
                    <CTableHeaderCell className="text-nowrap">Initiated By</CTableHeaderCell>
                    <CTableHeaderCell className="text-nowrap">Related Order ID</CTableHeaderCell>
                    <CTableHeaderCell className="text-nowrap">Order Creation</CTableHeaderCell>
                    <CTableHeaderCell className="text-nowrap">Completed At</CTableHeaderCell>
                    <CTableHeaderCell className="text-nowrap">Payment Status</CTableHeaderCell>
                    <CTableHeaderCell className="text-nowrap">Status Details</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {billingAttempts.map((attempt: BillingAttempt) => (
                    <CTableRow key={`${attempt.createdAt}-${attempt.orderId ?? 'no-order'}`}>
                      <CTableDataCell className="text-nowrap">
                        {formatDateTime(attempt.createdAt)}
                      </CTableDataCell>
                      <CTableDataCell>{getSourceDisplayValue(attempt.source)}</CTableDataCell>
                      <CTableDataCell>
                        {attempt.orderId ? (
                          <a
                            href={getShopifyOrderUrl(attempt.orderId) || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#0066cc', textDecoration: 'none' }}
                          >
                            {attempt.orderId}
                          </a>
                        ) : (
                          '-'
                        )}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={getBillingStatusBadgeColor(attempt.status)}>
                          {getStatusDisplayValue(attempt.status)}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell className="text-nowrap">
                        {formatDateTime(attempt.completedAt)}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={getPaymentStatusBadgeColor(attempt.paymentStatus)}>
                          {getPaymentStatusDisplayValue(attempt.paymentStatus)}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        {formatStatusDetails(
                          attempt.status,
                          attempt.paymentStatus,
                          attempt.errorCode,
                          attempt.errorMessage,
                          attempt.nextActionUrl,
                        )}
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
              <div className="d-flex justify-content-center mt-3">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  perPage={perPage}
                  total={total}
                  onPageChange={onPageChange}
                  onPerPageChange={onPerPageChange}
                />
              </div>
            </>
          ) : (
            <CAlert color="info">No billing attempts found for this customer.</CAlert>
          )}
        </>
      )}
    </>
  );
};
